import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";

const NAV_TREE = [
  { path: "/",           icon: "🏠", label: "Inicio" },
  { path: "/directo",    icon: "⚡", label: "Directo" },
  { path: "/comentarios",icon: "💬", label: "Comentarios" },
  { path: "/usuarios",   icon: "👥", label: "Usuarios" },
  { path: "/cajas",      icon: "📦", label: "Cajas" },
  { path: "/datos",      icon: "📊", label: "Datos" },
  { path: "/tv",         icon: "📺", label: "TV" },
  { path: "/config",     icon: "⚙️",  label: "Configuración" },
];

const PAGE_TITLES = {
  "/": "Inicio", "/directo": "Directo", "/comentarios": "Comentarios",
  "/usuarios": "Usuarios", "/cajas": "Cajas", "/datos": "Datos", "/tv": "TV",
};

export default function AppShell({ children }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { admin, logout } = useAuth();
  const { liveUser, joinLive, status } = useSocket();

  const [open, setOpen]           = useState(false);
  const [userInput, setUserInput] = useState("");
  const [connecting, setConnecting] = useState(false);

  const drawerRef    = useRef(null);
  const hamburgerRef = useRef(null);

  const isHome = location.pathname === "/";
  const title  = PAGE_TITLES[location.pathname] || "";

  useEffect(() => { if (open) setUserInput(liveUser || ""); }, [open, liveUser]);

  // Cerrar al click fuera del drawer Y fuera del botón hamburger
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (drawerRef.current?.contains(e.target)) return;
      if (hamburgerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Usar setTimeout para que no se ejecute en el mismo ciclo que abrió el drawer
    const tid = setTimeout(() => document.addEventListener("pointerdown", handler), 0);
    return () => { clearTimeout(tid); document.removeEventListener("pointerdown", handler); };
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleConnect = () => {
    const clean = userInput.trim();
    if (!clean) return;
    setConnecting(true);
    joinLive(clean);
    setTimeout(() => setConnecting(false), 1200);
  };

  const handleNav = (path) => { setOpen(false); navigate(path); };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const stateColor =
    status?.state === "online"     ? "#16a34a" :
    status?.state === "connecting" ? "#f59e0b" : "#94a3b8";

  const isTV = location.pathname === "/tv";

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>

      {/* Botón volver flotante solo en /tv */}
      {isTV && (
        <button
          onPointerDown={(e) => { e.preventDefault(); navigate(-1); }}
          style={{
            position: "fixed", top: 10, left: 10, zIndex: 999,
            background: "rgba(0,0,0,0.18)", border: "none", borderRadius: 10,
            color: "rgba(255,255,255,0.35)", fontSize: 20, padding: "8px 14px",
            cursor: "pointer", backdropFilter: "blur(4px)",
            touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
            transition: "opacity .2s",
          }}
          title="Volver"
        >←</button>
      )}

      {/* ── Header ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100, flexShrink: 0,
        display: isTV ? "none" : "flex", alignItems: "center", gap: 8,
        padding: "0 12px", height: 48,
        background: "rgba(255,255,255,0.9)", backdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--border)",
      }}>
        {/* Botón volver */}
        <div style={{ width: 80, flexShrink: 0 }}>
          {!isHome && (
            <button
              onPointerDown={(e) => { e.preventDefault(); navigate(-1); }}
              style={{
                background: "none", border: "1px solid var(--border)", borderRadius: 8,
                padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 700,
                color: "var(--text)", touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
                display: "flex", alignItems: "center", gap: 4,
              }}>
              ← Volver
            </button>
          )}
        </div>

        {/* Centro */}
        <div style={{ flex: 1, textAlign: "center", overflow: "hidden" }}>
          <span style={{ fontWeight: 800, fontSize: 15 }}>{title}</span>
          {liveUser && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              marginLeft: 8, fontSize: 11, fontWeight: 600, color: stateColor, verticalAlign: "middle",
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: stateColor, display: "inline-block", flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 100 }}>@{liveUser}</span>
            </span>
          )}
        </div>

        {/* Hamburger */}
        <div style={{ width: 80, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
          <button
            ref={hamburgerRef}
            onPointerDown={(e) => { e.preventDefault(); setOpen(v => !v); }}
            style={{
              background: open ? "#fff5f5" : "none",
              border: `1px solid ${open ? "var(--brand-red)" : "var(--border)"}`,
              borderRadius: 8, padding: "5px 11px", cursor: "pointer",
              fontSize: 18, lineHeight: 1, color: open ? "var(--brand-red)" : "var(--text)",
              touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
            }}>
            {open ? "✕" : "☰"}
          </button>
        </div>
      </header>

      {/* ── Overlay backdrop ── */}
      <div
        onPointerDown={() => setOpen(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 150,
          background: "rgba(0,0,0,0.4)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .2s",
        }}
      />

      {/* ── Drawer ── */}
      <div
        ref={drawerRef}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(300px, 85vw)", zIndex: 200,
          background: "#fff", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform .22s cubic-bezier(.4,0,.2,1)",
          display: "flex", flexDirection: "column", overflowY: "auto",
        }}>

        {/* Drawer header */}
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Menú</div>
          {admin?.username && <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>👤 {admin.username}</div>}
        </div>

        {/* Cambiar usuario TikTok */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            TikTok Live
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.4, fontSize: 12, pointerEvents: "none" }}>@</span>
              <input
                className="input"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                placeholder="usuario"
                style={{ paddingLeft: 22, fontSize: 13, padding: "8px 8px 8px 22px", width: "100%" }}
              />
            </div>
            <button
              className="btn btn-primary"
              disabled={!userInput.trim() || connecting}
              onPointerDown={(e) => { e.preventDefault(); handleConnect(); }}
              style={{ fontSize: 12, padding: "8px 12px", whiteSpace: "nowrap" }}>
              {connecting ? "…" : "Conectar"}
            </button>
          </div>
          {liveUser && (
            <div style={{ marginTop: 6, fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: stateColor, display: "inline-block", flexShrink: 0 }} />
              <span style={{ color: stateColor, fontWeight: 600 }}>{status?.state || "idle"}</span>
              <span style={{ opacity: 0.5 }}>· @{liveUser}</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ padding: "10px 10px", flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, paddingLeft: 6 }}>
            Pantallas
          </div>
          {NAV_TREE.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onPointerDown={(e) => { e.preventDefault(); handleNav(item.path); }}
                style={{
                  width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: active ? "#fff5f5" : "transparent",
                  color: active ? "var(--brand-red)" : "var(--text)",
                  fontWeight: active ? 800 : 500, fontSize: 15,
                  touchAction: "manipulation", WebkitTapHighlightColor: "transparent", marginBottom: 2,
                }}>
                <span style={{ fontSize: 20, width: 28, textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.tag && (
                  <span style={{ fontSize: 9, fontWeight: 800, background: "var(--brand-red)", color: "#fff", borderRadius: 4, padding: "1px 5px", textTransform: "uppercase" }}>
                    {item.tag}
                  </span>
                )}
                {active && <span style={{ color: "var(--brand-red)", fontSize: 10 }}>●</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding: "10px 16px 16px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
          <button
            className="btn btn-outline"
            onPointerDown={(e) => { e.preventDefault(); handleLogout(); }}
            style={{ width: "100%", fontSize: 13 }}>
            → Cerrar sesión
          </button>
        </div>
      </div>

      {/* ── Contenido ── */}
      <main style={{ flex: 1, padding: isTV ? 0 : "12px 24px 32px", width: "100%", boxSizing: "border-box" }}>
        {children}
      </main>
    </div>
  );
}
